import type { FirebaseOptions } from 'firebase/app';

export interface FirebaseConfigEnvironment {
  apiKey?: string;
  appId?: string;
  authDomain?: string;
  authEmulatorUrl?: string;
  messagingSenderId?: string;
  nodeEnv?: string;
  projectId?: string;
  storageBucket?: string;
}

const demoConfig = (projectId: string): FirebaseOptions => ({
  apiKey: 'demo-api-key',
  appId: 'demo-app-id',
  authDomain: `${projectId}.firebaseapp.com`,
  messagingSenderId: 'demo-messaging-sender-id',
  projectId,
  storageBucket: `${projectId}.appspot.com`,
});

export function resolveFirebaseConfig(
  environment: FirebaseConfigEnvironment,
): FirebaseOptions {
  const allowDemoValues =
    environment.nodeEnv === 'test' ||
    (environment.nodeEnv === 'development' && Boolean(environment.authEmulatorUrl));
  const projectId =
    environment.projectId ?? (allowDemoValues ? 'demo-kne-local' : undefined);

  const requiredValues = {
    NEXT_PUBLIC_FIREBASE_API_KEY: environment.apiKey,
    NEXT_PUBLIC_FIREBASE_APP_ID: environment.appId,
    NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN: environment.authDomain,
    NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID: environment.messagingSenderId,
    NEXT_PUBLIC_FIREBASE_PROJECT_ID: projectId,
    NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET: environment.storageBucket,
  };
  const missingVariables = Object.entries(requiredValues)
    .filter(([, value]) => !value)
    .map(([name]) => name);

  if (missingVariables.length > 0 && !allowDemoValues) {
    throw new Error(
      `Missing Firebase client configuration: ${missingVariables.join(', ')}`,
    );
  }

  const fallback = demoConfig(projectId ?? 'demo-kne-local');

  return {
    apiKey: environment.apiKey ?? fallback.apiKey,
    appId: environment.appId ?? fallback.appId,
    authDomain: environment.authDomain ?? fallback.authDomain,
    messagingSenderId:
      environment.messagingSenderId ?? fallback.messagingSenderId,
    projectId: projectId ?? fallback.projectId,
    storageBucket: environment.storageBucket ?? fallback.storageBucket,
  };
}
